/**
 * Mapa rozmiarów kontenerów SVG bryły pojazdu
 * w zależności od typu pojazdu i widoku (left/top/right).
 *
 * Wartości maxWidth/height są stosowane na kontenerze SVG via CSS variables:
 *   --vehicle-svg-max-w (mobile)
 *   --vehicle-svg-max-w-lg (desktop lg+)
 *   --vehicle-svg-h (mobile)
 *   --vehicle-svg-h-lg (desktop lg+)
 *
 * Globalny CSS rule `.vehicle-svg-container` w index.css
 * automatycznie przełącza wartości na odpowiednim breakpoincie.
 */

export type VehicleType = 'sedan' | 'van' | 'hatchback';
export type VehicleViewPosition = 'left' | 'top' | 'right';

export interface VehicleSvgSize {
  /** Max-width kontenera SVG na mobile/tablet (px) */
  maxWidth: number;
  /** Max-width kontenera SVG na desktop lg+ (px) */
  maxWidthLg: number;
  /** Wysokość kontenera SVG na mobile (px) */
  height: number;
  /** Wysokość kontenera SVG na desktop lg+ (px) */
  heightLg: number;
  /** Margines kontenera SVG na mobile (CSS shorthand, np. "10px 20px") */
  margin?: string;
  /** Margines kontenera SVG na desktop lg+ (CSS shorthand) */
  marginLg?: string;
}

type VehicleSvgSizeMap = Record<VehicleType, Record<VehicleViewPosition, VehicleSvgSize>>;

export const vehicleSvgSizeMap: VehicleSvgSizeMap = {
  sedan: {
    left: { maxWidth: 208, maxWidthLg: 208, height: 720, heightLg: 720, margin: '0 auto', marginLg: '0 auto 0 19%' },
    top: { maxWidth: 290, maxWidthLg: 290, height: 720, heightLg: 720, margin: '0 auto', marginLg: '0 auto' },
    right: { maxWidth: 208, maxWidthLg: 208, height: 720, heightLg: 720, margin: '0 auto', marginLg: '0 auto 0 29%' },
  },
  van: {
    left: { maxWidth: 264, maxWidthLg: 264, height: 720, heightLg: 720, margin: '0 auto', marginLg: '0 auto 0 20%' },
    top: { maxWidth: 303, maxWidthLg: 303, height: 720, heightLg: 720, margin: '0 auto', marginLg: '0 auto' },
    right: { maxWidth: 264, maxWidthLg: 264, height: 720, heightLg: 720, margin: '0 auto', marginLg: '0 auto 0 18%' },
  },
  hatchback: {
    left: { maxWidth: 233, maxWidthLg: 233, height: 720, heightLg: 720, margin: '0 auto', marginLg: '0 auto 0 20%' },
    top: { maxWidth: 342, maxWidthLg: 342, height: 720, heightLg: 720, margin: '0 auto', marginLg: '0 auto' },
    right: { maxWidth: 233, maxWidthLg: 233, height: 720, heightLg: 720, margin: '0 auto', marginLg: '0 auto 0 25%' },
  },
};

/**
 * Zwraca rozmiar SVG dla danego typu pojazdu i widoku.
 */
export function getVehicleSvgSize(
  vehicleType: VehicleType,
  view: VehicleViewPosition,
): VehicleSvgSize {
  return vehicleSvgSizeMap[vehicleType][view];
}

/**
 * Zwraca obiekt CSS variables do ustawienia na kontenerze SVG.
 * Używaj razem z className="vehicle-svg-container".
 */
export function getVehicleSvgStyle(
  vehicleType: VehicleType,
  view: VehicleViewPosition,
): React.CSSProperties {
  const size = getVehicleSvgSize(vehicleType, view);
  const lgMargin =
    view === 'right' ? '0 0 0 auto' : view === 'left' ? '0 auto 0 0' : '0 auto';
  return {
    '--vehicle-svg-max-w': `${size.maxWidth}px`,
    '--vehicle-svg-max-w-lg': `${size.maxWidthLg}px`,
    '--vehicle-svg-h': `${size.height}px`,
    '--vehicle-svg-h-lg': `${size.heightLg}px`,
    '--vehicle-svg-m': size.margin ?? '0 auto',
    '--vehicle-svg-m-md': lgMargin,
    '--vehicle-svg-m-lg': size.marginLg ?? lgMargin,
  } as React.CSSProperties;
}

export const vehiclePaintPointsSvgSizeMap: VehicleSvgSizeMap = {
  sedan: {
    left: { maxWidth: 200, maxWidthLg: 208, height: 720, heightLg: 720, margin: '0 auto', marginLg: '0 auto 0 19%' },
    top: { maxWidth: 235, maxWidthLg: 290, height: 720, heightLg: 720, margin: '0 auto', marginLg: '0 auto' },
    right: { maxWidth: 200, maxWidthLg: 208, height: 720, heightLg: 720, margin: '0 auto', marginLg: '0 auto 0 29%' },
  },
  van: {
    left: { maxWidth: 260, maxWidthLg: 264, height: 720, heightLg: 720, margin: '0 auto', marginLg: '0 auto 0 20%' },
    top: { maxWidth: 235, maxWidthLg: 303, height: 720, heightLg: 720, margin: '0 auto', marginLg: '0 auto' },
    right: { maxWidth: 260, maxWidthLg: 264, height: 720, heightLg: 720, margin: '0 auto', marginLg: '0 auto 0 18%' },
  },
  hatchback: {
    left: { maxWidth: 225, maxWidthLg: 233, height: 720, heightLg: 720, margin: '0 auto', marginLg: '0 auto 0 20%' },
    top: { maxWidth: 235, maxWidthLg: 342, height: 720, heightLg: 720, margin: '0 auto', marginLg: '0 auto' },
    right: { maxWidth: 225, maxWidthLg: 233, height: 720, heightLg: 720, margin: '0 auto', marginLg: '0 auto 0 25%' },
  },
};

export function getVehiclePaintPointsSvgSize(
  vehicleType: VehicleType,
  view: VehicleViewPosition,
): VehicleSvgSize {
  return vehiclePaintPointsSvgSizeMap[vehicleType][view];
}

export function getVehiclePaintPointsSvgStyle(
  vehicleType: VehicleType,
  view: VehicleViewPosition,
): React.CSSProperties {
  const size = getVehiclePaintPointsSvgSize(vehicleType, view);
  const lgMargin =
    view === 'right' ? '0 auto' : view === 'left' ? '0 auto' : '0 auto';
  return {
    '--vehicle-svg-max-w': `${size.maxWidth}px`,
    '--vehicle-svg-max-w-lg': `${size.maxWidthLg}px`,
    '--vehicle-svg-h': `${size.height}px`,
    '--vehicle-svg-h-lg': `${size.heightLg}px`,
    '--vehicle-svg-m': size.margin ?? '0 auto',
    '--vehicle-svg-m-md': lgMargin,
    '--vehicle-svg-m-lg': size.marginLg ?? lgMargin,
  } as React.CSSProperties;
}
